import React, { useState, useEffect, useContext } from 'react';
import styles from './styles/FolderModal.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';
import profileIcon from '../../images/profileicon.png';

const FolderModal = ({
    isOpen,
    onClose,
    mode,            
    folder,          
    parentFolder,    
    onFolderCreated,
    onFolderUpdated,
    onFolderDeleted,
}) => {
    const { classroom, createFolder, updateFolder, deleteFolder } = useContext(ClassroomContext);

    const [folderName, setFolderName] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [isPrivate, setIsPrivate] = useState(false);
    const [error, setError] = useState(null);
    const [closing, setClosing] = useState(false); 

    
    
    const parentIsPresent = !!parentFolder;
    const parentIsPrivate = parentFolder?.isPrivate || false;
    const parentAllowedStudents = parentFolder?.allowedStudents || [];

    
    
    
    const allPossibleStudents = parentIsPresent
        ? parentAllowedStudents
        : classroom?.students || [];

    useEffect(() => {
        if (!isOpen && !closing) return;
    
        if (mode === 'edit' && folder) {
            
            setFolderName(folder.name || '');
            setIsPrivate(folder.isPrivate || false);
    
            
            let currentAllowed = (folder.allowedStudents || []).filter(st => st && st._id);
    
            if (parentIsPresent) {
                
                const parentAllowedIds = parentAllowedStudents
                    .filter(st => st && st._id) 
                    .map(st => st._id.toString());
    
                currentAllowed = currentAllowed.filter(st =>
                    parentAllowedIds.includes(st._id.toString())
                );
            }
    
            
            setSelectedStudents(currentAllowed.map(st => st._id));
        } else {
            
            setFolderName('');
    
            
            if (parentIsPresent && parentIsPrivate) {
                setIsPrivate(true);
    
                
                const allIds = parentAllowedStudents
                    .filter(st => st && st._id)
                    .map(st => st._id);
                setSelectedStudents(allIds);
            } else {
                
                setIsPrivate(false);
                setSelectedStudents([]);
            }
        }
    }, [isOpen, folder, parentFolder, mode]);
        

    const handleNameChange = (e) => setFolderName(e.target.value);

    const toggleStudentSelection = (studentId) => {
        if (selectedStudents.includes(studentId)) {
            setSelectedStudents(selectedStudents.filter((id) => id !== studentId));
        } else {
            setSelectedStudents([...selectedStudents, studentId]);
        }
    };

    const handleIsPrivateChange = (e) => {
        const newVal = e.target.checked;
        setIsPrivate(newVal);

        
        if (newVal) {
            const ids = allPossibleStudents.map(s => s._id);
            setSelectedStudents(ids);
        } else {
            
            setSelectedStudents([]);
        }
    };

    const handleSubmit = async () => {
        if (!folderName.trim()) {
            setError('Folder name cannot be empty');
            return;
        }
        setError(null);

        
        const parentFolderId = parentIsPresent ? parentFolder._id : null;

        const folderData = {
            name: folderName,
            classroomId: classroom._id,
            allowedStudents: isPrivate ? selectedStudents : [],
            isPrivate,
            parentFolderId,
        };

        try {
            if (mode === 'create') {
                const newFolder = await createFolder(folderData);
                if (onFolderCreated) {
                    onFolderCreated(newFolder);
                }
            } else if (mode === 'edit') {
                const updatedFolder = await updateFolder(folder._id, folderData);
                if (onFolderUpdated) {
                    onFolderUpdated(updatedFolder);
                }
            }
            triggerClose();
        } catch (error) {
            console.error('Error in FolderModal submit:', error);
            setError('An error occurred. Please try again.');
        }
    };

    const handleDelete = async () => {
        if (!folder) return;
        try {
            await deleteFolder(folder._id);
            if (onFolderDeleted) {
                onFolderDeleted(folder._id);
            }
            triggerClose();
        } catch (error) {
            console.error('Error deleting folder:', error);
            setError('An error occurred. Please try again.');
        }
    };

    const triggerClose = () => {
        setClosing(true);
        setTimeout(() => {
            onClose();
            setClosing(false);
        }, 300); 
    };

    if (!isOpen && !closing) return null;

    return (
        <div className={`${styles.modalOverlay} ${closing ? styles.fadeOut : styles.fadeIn}`}>
            <div className={styles.modalContent}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        {mode === 'create' ? 'Create Folder' : 'Edit Folder'}
                    </h2>
                </div>

                {/* Body */}
                <div className={styles.modalBody}>
                    <label htmlFor="folderName" className={styles.label}>
                        Folder Name
                    </label>
                    <input
                        type="text"
                        id="folderName"
                        value={folderName}
                        onChange={handleNameChange}
                        className={styles.modalInput}
                    />

                    <div className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            id="isPrivate"
                            checked={isPrivate}
                            onChange={handleIsPrivateChange}
                            disabled={parentIsPrivate} 
                            
                        />
                        <label htmlFor="isPrivate">Private Folder</label>
                    </div>

                    {isPrivate && (
                        <>
                            <div className={styles.listHeader}>
                                Select students to have access:
                            </div>
                            <ul className={styles.list}>
                                {allPossibleStudents.map((student) => {
                                    const selected = selectedStudents.includes(student._id);
                                    return (
                                        <li
                                            key={student._id}
                                            onClick={() => toggleStudentSelection(student._id)}
                                            className={`${styles.item} ${selected ? styles.selectedItem : ''}`}
                                        >
                                            <img
                                                src={profileIcon}
                                                alt="User Icon"
                                                className={styles.profileIcon}
                                            />
                                            {student.username}
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                    {error && <div className={styles.error}>{error}</div>}
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    <div className={styles.buttonContainer}>
                        <button
                            className={`${styles.buttonBase} ${styles.saveButton}`}
                            onClick={handleSubmit}
                        >
                            {mode === 'create' ? 'Create' : 'Save'}
                        </button>
                        {mode === 'edit' && folder && (
                            <button
                                onClick={handleDelete}
                                className={`${styles.buttonBase} ${styles.deleteButton}`}
                            >
                                Delete
                            </button>
                        )}
                        <button
                            className={`${styles.buttonBase} ${styles.cancelButton}`}
                            onClick={triggerClose}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FolderModal;
